import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Calendar, 
  Plus, 
  Search,
  Eye,
  Trash2,
  Loader2,
  Users,
  Clock,
  User
} from 'lucide-react';

const ConferenceSetsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSchoolAdmin = user?.role === 'school_admin';
  const [conferenceSets, setConferenceSets] = useState([]);
  const [schoolLevels, setSchoolLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [newSet, setNewSet] = useState({
    friendly_name: '',
    school_level_id: 0,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [confSetsRes, schoolRes] = await Promise.all([
        api.get('/conference-sets'),
        api.get('/auth/me').then(res => api.get(`/schools/${res.data.school_id}/levels`))
      ]);
      setConferenceSets(confSetsRes.data);
      setSchoolLevels(schoolRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newSet.friendly_name) {
      toast.error('Please enter a name');
      return;
    }

    setFormLoading(true);
    try {
      const response = await api.post('/conference-sets', newSet);
      toast.success('Conference set created');
      setCreateDialogOpen(false);
      setNewSet({
        friendly_name: '',
        school_level_id: 0,
        notes: ''
      });
      navigate(`/conference-sets/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this conference set?')) return;
    
    try {
      await api.delete(`/conference-sets/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const filteredSets = conferenceSets.filter(cs => 
    cs.friendly_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="conference-sets-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-slate-900">Conference Sets</h1>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">Manage parent-teacher conference configurations</p>
          </div>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
            onClick={() => setCreateDialogOpen(true)}
            data-testid="create-conference-set-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Conference Set
          </Button>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search conference sets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-sm"
                data-testid="search-conference-sets"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-outfit text-lg sm:text-xl">All Conference Sets</CardTitle>
            <CardDescription className="text-sm">{filteredSets.length} set(s)</CardDescription>
          </CardHeader>
          <CardContent className="p-0 sm:p-6">
            {filteredSets.length === 0 ? (
              <div className="text-center py-12 px-4">
                <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No conference sets yet</p>
                <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
                  Create your first set
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm">Name</TableHead>
                      <TableHead className="text-xs sm:text-sm hidden sm:table-cell">School Level</TableHead>
                      {isSchoolAdmin && <TableHead className="text-xs sm:text-sm hidden md:table-cell">Owner</TableHead>}
                      <TableHead className="text-xs sm:text-sm">Teachers</TableHead>
                      <TableHead className="text-xs sm:text-sm hidden sm:table-cell">Schedules</TableHead>
                      <TableHead className="text-right text-xs sm:text-sm">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSets.map((cs) => (
                      <TableRow key={cs.id} data-testid={`conf-set-${cs.id}`}>
                        <TableCell className="font-medium text-sm">{cs.friendly_name}</TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <Badge variant="outline" className="text-xs">{cs.school_level_description}</Badge>
                        </TableCell>
                        {isSchoolAdmin && (
                          <TableCell className="hidden md:table-cell">
                          {cs.owner_name ? (
                            <Badge variant="secondary" className="flex items-center gap-1 w-fit text-xs">
                              <User className="h-3 w-3" />
                              {cs.owner_name}
                            </Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-1 text-slate-600 text-sm">
                          <Users className="h-4 w-4 flex-shrink-0" />
                          {cs.teacher_ids?.length || 0}
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <div className="flex items-center gap-1 text-slate-600 text-sm">
                          <Clock className="h-4 w-4 flex-shrink-0" />
                          {cs.schedule_set_ids?.length || 0}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/conference-sets/${cs.id}`)}
                            data-testid={`view-conf-set-${cs.id}`}
                            className="h-8 w-8 p-0"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-rose-600 h-8 w-8 p-0"
                            onClick={() => handleDelete(cs.id)}
                            data-testid={`delete-conf-set-${cs.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-outfit">Create Conference Set</DialogTitle>
              <DialogDescription>
                Set up a new parent-teacher conference configuration
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={newSet.friendly_name}
                  onChange={(e) => setNewSet({ ...newSet, friendly_name: e.target.value })}
                  placeholder="Fall 2024 Conferences"
                  data-testid="conf-set-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>School Level</Label>
                <Select
                  value={String(newSet.school_level_id)}
                  onValueChange={(v) => setNewSet({ ...newSet, school_level_id: parseInt(v) })}
                >
                  <SelectTrigger data-testid="conf-set-level-select">
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    {schoolLevels.map((level) => (
                      <SelectItem key={level.level_id} value={String(level.level_id)}>
                        {level.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={newSet.notes}
                  onChange={(e) => setNewSet({ ...newSet, notes: e.target.value })}
                  placeholder="Optional notes..."
                  data-testid="conf-set-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={handleCreate}
                disabled={formLoading}
                data-testid="submit-create-conf-set"
              >
                {formLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ConferenceSetsPage;
